/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNew from '@mui/icons-material/OpenInNew';
import {
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  tableCellClasses,
} from '@mui/material';
import { isNil } from 'lodash';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { updateServiceTierRequest } from '../../../store/serviceTierRequests/actions';
import { dateToUTCDate } from '../../../utils/DateTimeUtils';
import Scrollbars from '../../common/layout/Scrollbars';
import MenuItems from '../../common/layout/menu/MenuItems';
import appTheme from '../../common/theme/appTheme.json';
import ServiceTierRequest, { ServiceTierRequestStatus } from '../../../shared/entities/serviceTierRequest';
import ModalDialog from '../../common/dialogs/ModalDialog';
import { selectTerminalsWithTerminalUuidKey } from '../../../store';

const styleCell = {
  overflow: 'hidden',
  padding: '0px 16px',
  lineHeight: '16px',
  verticalAlign: 'center',
};
const TABLE_ROW_HEIGHT = 52;
const MAX_SERVICES_PER_PAGE = 15;
const SCROLLABLE_HEIGHT = 55 + MAX_SERVICES_PER_PAGE * TABLE_ROW_HEIGHT;

interface ServiceTierRequestsTableProps {
  serviceRequests: ServiceTierRequest[];
}
const ServiceTierRequestsTable = ({ serviceRequests }: ServiceTierRequestsTableProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  // need to map the terminalUuid to the datapathUuid
  const terminalUuidMap = useSelector(selectTerminalsWithTerminalUuidKey, shallowEqual);

  //local states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clickedRequestId, setClickedRequestId] = useState<string>();
  const [clickedServiceStatus, setClickedServiceStatus] = useState<ServiceTierRequestStatus>();

  const approveOrDenyBtnClickHandler = (requestId: string, status: ServiceTierRequestStatus) => {
    if (requestId && status) {
      setDialogOpen(true);
      setClickedRequestId(requestId);
      setClickedServiceStatus(status);
    }
  };
  const approveOrDenyServiceChangeRequest = (requestId: string, status: ServiceTierRequestStatus) => {
    if (requestId && status) {
      dispatch(updateServiceTierRequest(requestId, status));
      setDialogOpen(false);
    }
  };
  const terminalNameClickHandler = (terminalUuid: string) => {
    if (!isNil(terminalUuid) && !isNil(terminalUuidMap[terminalUuid]?.datapathUuid)) {
      history.push(`/${MenuItems.fleet}/${terminalUuidMap[terminalUuid].datapathUuid}`);
    }
  };
  return (
    <Scrollbars autoHeight autoHeightMax={SCROLLABLE_HEIGHT}>
      <TableContainer component={Paper}>
        <Table stickyHeader aria-label="service tier table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Terminal name</StyledTableCell>
              <StyledTableCell align="left" sx={{ minWidth: '200px' }}>
                Requested service tier
              </StyledTableCell>
              <StyledTableCell align="left" sx={{ minWidth: '120px' }}>
                Organization ID
              </StyledTableCell>
              <StyledTableCell align="left">Requester</StyledTableCell>
              <StyledTableCell align="left" sx={{ minWidth: '100px' }}>
                Request date
              </StyledTableCell>
              <StyledTableCell align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {serviceRequests.length > 0 &&
              serviceRequests.map(service => (
                <TableRow key={service.id} sx={{ height: TABLE_ROW_HEIGHT }}>
                  <TableCell sx={{ ...styleCell }}>
                    <Link
                      target="_blank"
                      href="#"
                      underline="hover"
                      component="button"
                      onClick={() => terminalNameClickHandler(service.terminalId)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        color: appTheme.textLinkColor,
                        fontSize: '16px',
                      }}
                    >
                      {service.terminalDisplayName}
                      <OpenInNew fontSize="inherit" sx={{ pl: 1, color: '#ffffff' }} />
                    </Link>
                  </TableCell>
                  <TableCell sx={{ ...styleCell }} align="left">
                    {service.requestedServiceTier}
                  </TableCell>
                  <TableCell sx={{ ...styleCell }} align="left">
                    {service.requesterOrgId}
                  </TableCell>
                  <TableCell sx={{ ...styleCell }} align="left">
                    <Link
                      target="_blank"
                      href={`mailto:${service.requesterEmail}`}
                      underline="hover"
                      component="a"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        color: appTheme.textLinkColor,
                      }}
                    >
                      {service.requesterFullName}
                      <EmailIcon fontSize="inherit" sx={{ pl: 1, color: '#ffffff' }} />
                    </Link>
                  </TableCell>
                  <TableCell sx={{ ...styleCell }} align="left">
                    {dateToUTCDate(new Date(service.submittedDate), true)}
                  </TableCell>
                  <TableCell sx={{ ...styleCell }} align="left">
                    <Stack direction="row" spacing={2}>
                      <IconButton
                        onClick={() => approveOrDenyBtnClickHandler(service.id, ServiceTierRequestStatus.COMPLETED)}
                      >
                        <CheckCircleIcon sx={{ color: appTheme.standbyStatusColor }} />
                      </IconButton>
                      <IconButton
                        onClick={() => approveOrDenyBtnClickHandler(service.id, ServiceTierRequestStatus.DENIED)}
                      >
                        <CancelIcon sx={{ color: appTheme.errorTextColor }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {serviceRequests.length === 0 && (
        <Typography align="center" mt={5} color={appTheme.disabledTextColor}>
          No service tier requests to display.
        </Typography>
      )}
      <ModalDialog
        open={dialogOpen}
        leftBtnText="Cancel"
        rightBtnText="OK"
        message={`This action will mark this item as ${
          clickedServiceStatus === ServiceTierRequestStatus.COMPLETED ? 'approved' : 'denied'
        } and remove it from the Service Tier Request list.`}
        onLeftBtnClicked={() => setDialogOpen(false)}
        onRightBtnClicked={() => approveOrDenyServiceChangeRequest(clickedRequestId!, clickedServiceStatus!)}
      />
    </Scrollbars>
  );
};

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: appTheme.primaryBackgroundColor,
    color: '#9FAFBC',
    paddingLeft: '12px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    fontWeight: 600,
    fontFamily: appTheme.appFontFamily,
    lineHeight: 24,
  },
}));

export default ServiceTierRequestsTable;
